import { useRef } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { ChevronDown, SearchIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Checkbox } from '@src/design-system/checkbox';
import { cn } from '@src/design-system/helpers';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';

import type { TProgram } from '@src/shared/types/program';
import type { TMember } from '@src/shared/types/member';
import { isTMember, isTProgram } from '../helper/type-checker';
import { FilterSearch } from 'iconsax-react';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import type createCrudService from '@core/api/create-crud-service';

type ItemType = TProgram | TMember;

export const MultiSelectFilter = ({
  fetchMethod,
  filterName,
  placeHolder,
  className,
  label,
}: {
  fetchMethod: ReturnType<
    typeof createCrudService<ItemType, Record<string, unknown>>
  >['useFetchInfinite'];
  filterName: string;
  placeHolder: string;
  className?: string;
  label: string;
}) => {
  const [params, setParams] = useSearchParams();

  const selectedPrograms = params.getAll(filterName);
  const isAllSelected = selectedPrograms.length === 0;
  const lastSelectionRef = useRef<string[]>([]);

  const handleToggle = (id: string) => {
    const current = params.getAll(filterName);
    let updated: string[];
    if (current.includes(id)) {
      updated = current.filter((p) => p !== id);
    } else {
      updated = [...current, id];
    }
    lastSelectionRef.current = updated;
    const newParams = new URLSearchParams(params);
    newParams.delete(filterName);
    updated.forEach((id) => newParams.append(filterName, id));
    setParams(newParams);
  };

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          'w-fit border-primary-200 bg-white border-1 rounded-[8px] px-3 hover:bg-primary-50 cursor-pointer',
          className
        )}
      >
        <div className='flex items-center gap-2'>
          <FilterSearch
            variant='TwoTone'
            size={24}
            className='text-gray-500'
          />
          <span className='truncate max-w-[200px] text-gray-800 text-sm font-medium'>
            {placeHolder}{' '}
            {selectedPrograms.length > 0 && (
              <span className='ml-auto'>({selectedPrograms.length})</span>
            )}
          </span>
          <ChevronDown
            className='text-gray-800'
            size={16}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent
        style={{ width: 240 }}
        className='p-0 z-50 bg-white'
      >
        <InfiniteList<ItemType, Record<string, unknown>>
          allItem={
            <label className='flex items-center gap-2 p-2 cursor-pointer hover:bg-[#0A0F290A] rounded-lg'>
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={(checked) => {
                  if (checked) {
                    // Save current selection then clear params
                    lastSelectionRef.current = selectedPrograms;
                    const newParams = new URLSearchParams(params);
                    newParams.delete(filterName);
                    setParams(newParams);
                  } else {
                    // Restore last non-empty selection
                    const restore = lastSelectionRef.current || [];
                    const newParams = new URLSearchParams(params);
                    newParams.delete(filterName);
                    restore.forEach((id) => newParams.append(filterName, id));
                    setParams(newParams);
                  }
                }}
                className='border border-[#DEE0E3]'
              />
              <span className='truncate text-gray-900 font-normal text-sm'>
                All {label}
              </span>
            </label>
          }
          fetchInfiniteData={fetchMethod}
          enableSearch={true}
          props={{
            listContainerProps: {
              className: 'max-h-[300px]',
            },
            listProps: {
              className: 'gap-0',
            },
            searchInputProps: {
              placeholder: 'Search...',
              prefixIcon: (
                <SearchIcon className='size-4 shrink-0 text-[#211835]' />
              ),
              className: 'bg-[#FCFAFF]',
            },
          }}
          renderItem={(item) => {
            const id = item.id.toString();
            const isChecked = selectedPrograms.includes(id);

            // Get full name for tooltip
            const fullName = isTProgram(item)
              ? item.name
              : isTMember(item)
                ? `${item.first_name} ${item.last_name}`
                : '';

            return (
              <label
                key={id}
                className='flex items-center gap-2 p-2 cursor-pointer hover:bg-[#0A0F290A] rounded-lg'
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => handleToggle(id)}
                  className='border border-[#DEE0E3]'
                />
                {isTProgram(item) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='truncate text-gray-900 font-normal text-sm'>
                        {item.name}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side='top'>{fullName}</TooltipContent>
                  </Tooltip>
                )}
                {isTMember(item) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className='flex items-center gap-2 flex-1 min-w-0'>
                        <Avatar className='size-5 shrink-0'>
                          <AvatarImage src={item.avatar} />
                          <AvatarFallback className='text-gray-900 font-normal text-xs'>
                            {`${item.first_name} ${item.last_name}`.substring(
                              0,
                              2
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <span className='truncate text-gray-900 font-normal text-sm'>
                          {item.first_name} {item.last_name}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side='top'>{fullName}</TooltipContent>
                  </Tooltip>
                )}
              </label>
            );
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectFilter;
